import { useEffect, useState } from "react";
import type { Rpc } from "../lib/rpc";

type CredentialsBag = Record<string, Record<string, string>>;

const SERVICE_LABELS: Record<string, { name: string; icon: string }> = {
  twitter: { name: "Twitter / X", icon: "𝕏" },
  fetlife: { name: "FetLife",     icon: "FL" },
  redgifs: { name: "RedGifs",     icon: "RG" },
  reddit:  { name: "Reddit",      icon: "R" },
  email:   { name: "Email (Gmail)", icon: "📧" },
  gemini:  { name: "Gemini AI",   icon: "🤖" },
};

const SECRET_FIELDS = new Set(["password", "api_key"]);

function fmtField(name: string): string {
  return name.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export function CredentialsPage({ rpc, electronConnected }: { rpc: Rpc; electronConnected: boolean }) {
  const [creds, setCreds] = useState<CredentialsBag | null>(null);
  const [encrypted, setEncrypted] = useState<boolean>(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!electronConnected) { setCreds(null); return; }
    let cancelled = false;
    async function load() {
      try {
        const r = await rpc.call<{ ok: boolean; data?: CredentialsBag; encrypted?: boolean; accountId?: string; error?: string }>("get-credentials");
        if (cancelled) return;
        if (!r.ok) { setErr(r.error || "Failed to load"); return; }
        setCreds(r.data || {});
        setEncrypted(!!r.encrypted);
        setAccountId(r.accountId || null);
        setErr(null);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    }
    load();
    // Refetch when the active account changes so we always show the right
    // account's creds — main.js mirrors them to the legacy file on switch.
    const off = rpc.onEvent("account-switched", () => { setCreds(null); load(); });
    return () => { cancelled = true; off(); };
  }, [rpc, electronConnected]);

  function update(service: string, field: string, value: string) {
    setCreds((prev) => prev && ({ ...prev, [service]: { ...prev[service], [field]: value } }));
  }
  function toggle(service: string, field: string) {
    const k = `${service}.${field}`;
    setRevealed((prev) => ({ ...prev, [k]: !prev[k] }));
  }
  async function save() {
    if (!creds) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await rpc.call<{ ok: boolean; error?: string }>("save-credentials", creds);
      if (!r.ok) throw new Error(r.error || "save failed");
      setSavedAt(Date.now());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (!electronConnected) {
    return (
      <section className="settings-page">
        <header className="settings-page-header"><h2>🔑 Credentials</h2></header>
        <div className="empty">Electron must be running on the worker PC.</div>
      </section>
    );
  }
  if (!creds) {
    return (
      <section className="settings-page">
        <header className="settings-page-header"><h2>🔑 Credentials</h2></header>
        <div className="empty">{err || "Loading…"}</div>
      </section>
    );
  }

  const services = Object.keys(creds);

  return (
    <section className="settings-page">
      <header className="settings-page-header">
        <h2>🔑 Credentials</h2>
        <div className="header-actions">
          {accountId && <span className="account-chip">account: {accountId}</span>}
          <span className={`enc-badge ${encrypted ? "enc-on" : "enc-off"}`}>
            {encrypted ? "🔒 DPAPI encrypted (local)" : "⚠️ Plaintext on disk"}
          </span>
          {savedAt && Date.now() - savedAt < 4000 && <span className="ok">✓ saved</span>}
          {err && <span className="err">{err}</span>}
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "💾 Save"}
          </button>
        </div>
      </header>

      <p className="hint">
        Stored encrypted on the worker PC via Windows DPAPI (only your Windows
        login can decrypt). Sent to your dashboard browser in cleartext over
        the bearer-token-protected channel — fine for personal use, just don't
        share the token.
      </p>

      <div className="credentials-list">
        {services.map((service) => {
          const label = SERVICE_LABELS[service] || { name: service, icon: "🔑" };
          const fields = Object.entries(creds[service] || {});
          return (
            <div key={service} className="cred-card">
              <header className="cred-card-header">
                <span className="cred-icon">{label.icon}</span>
                <h3>{label.name}</h3>
              </header>
              <div className="cred-fields">
                {fields.map(([field, value]) => {
                  const isSecret = SECRET_FIELDS.has(field);
                  const shown = revealed[`${service}.${field}`];
                  return (
                    <div key={field} className="cred-field">
                      <label>{fmtField(field)}</label>
                      <div className="cred-input-wrap">
                        <input
                          type={isSecret && !shown ? "password" : "text"}
                          value={value}
                          spellCheck={false}
                          autoComplete="off"
                          onChange={(e) => update(service, field, e.target.value)}
                        />
                        {isSecret && (
                          <button
                            type="button"
                            className="toggle-vis"
                            onClick={() => toggle(service, field)}
                          >
                            {shown ? "🙈" : "👁"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
